import { useEffect, useState } from "react";

// ASSETS

// STYLES
import { LandscapeOverlay, LandscapeMessage, GlobalStyle } from "./AppStyled";

// LIBRARIES
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { useTranslation } from "react-i18next";

//REDUX
import { useAppSelector } from "hooks/reduxHooks";
import { useDispatch, useSelector } from "react-redux";
import { handleChangeSnackBar } from "slices/SessionSlice/sessionSlice";
import {
  isRequestLoadingSelector,
  snackBar,
  snackBarAlert,
} from "slices/SessionSlice/sessionSliceSelectors";

// MISC
import AppRoutes from "./config/AppRoutes";
import useCookieConsent from "view/CookieModule/hooks/useCookieConsent";
import {
  initialAnalyticsCookies,
  initialNecessaryCookies,
} from "view/CookieModule/config/cookieModuleConfig";
import { COLORS } from "constants/variables";

// COMPONENTS
import LoadingAnimation from "components/Atoms/LoadingAnimation/LoadingAnimation";
import CookieConsent from "view/CookieModule/components/CookieConsent/CookieConsent";
import useCookieChangeListener from "view/CookieModule/hooks/useCookieChangeListener";
import {
  disableGoogleAnalytics,
  initializeGoogleAnalytics,
} from "view/CookieModule/helpers/googleAnalyticsHelper";
import { selectLanguage } from "slices/LanguageSlice/languageSlice";

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
// CONFIGURATION

const App = () => {
  // API REQUESTS

  // LIBRARY CONSTANTS
  const dispatch = useDispatch();
  const snackBarData = useSelector(snackBar);
  const snackBarAlertData = useSelector(snackBarAlert);
  const isRequestLoadingData = useAppSelector(isRequestLoadingSelector);
  const currentLanguage = useSelector(selectLanguage);
  const { t } = useTranslation();

  // STATE CONSTANTS
  const [isLandscape, setIsLandscape] = useState(false);

  // LIFE CYCLE
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(isMobileLandscape());
    };
    handleOrientationChange();
    window.addEventListener("resize", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  // EVENT HANDLERS
  const handleClose = (_, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch(
      handleChangeSnackBar({
        snackBar: { open: false, autoHideDuration: 2000 },
      })
    );
  };

  function isMobileLandscape() {
    const maxWidthForMobile = 1023;

    const minHeightForLandscape = 600;

    const isLandscape = window.innerWidth > window.innerHeight;

    const isMobileDevice = window.innerWidth <= maxWidthForMobile;

    const isHeightEligibleForLandscape =
      window.innerHeight > minHeightForLandscape;

    return isLandscape && isMobileDevice && !isHeightEligibleForLandscape;
  }

  useCookieChangeListener((necessaryCookies, analyticsCookies) => {
    console.log("Cookies have changed:", {
      necessaryCookies,
      analyticsCookies,
    });
    const isGoogleAnalyticsEnabled = analyticsCookies.find(
      (cookie) => cookie.title === "_ga" && cookie.checked
    );
    if (isGoogleAnalyticsEnabled) {
      initializeGoogleAnalytics();
      console.log("Google Analytics enabled");
    } else {
      disableGoogleAnalytics();
      console.log("Google Analytics disabled");
    }
    // Add other cookie-based actions here
  });

  const { handleSaveSettings } = useCookieConsent(
    initialNecessaryCookies,
    initialAnalyticsCookies
  );

  return (
    <>
      <GlobalStyle />
      <SkeletonTheme
        baseColor={COLORS.Cultured}
        highlightColor={COLORS.Background}
      >
        <LandscapeOverlay>
          <BrowserRouter>
            <Routes>
              <Route path={"/*"} element={<AppRoutes />} />
            </Routes>
            <Snackbar {...snackBarData} onClose={handleClose}>
              <MuiAlert {...snackBarAlertData} onClose={handleClose}>
                {snackBarAlertData?.message}
              </MuiAlert>
            </Snackbar>

            <CookieConsent
              language={currentLanguage}
              necessaryCookies={initialNecessaryCookies}
              analyticsCookies={initialAnalyticsCookies}
              onSave={handleSaveSettings}
            />

            {isRequestLoadingData && <LoadingAnimation />}
          </BrowserRouter>
        </LandscapeOverlay>
      </SkeletonTheme>
    </>
  );
};

export default App;
